import {isEqual} from 'lodash';
import MapboxCompare from 'mapbox-gl-compare';
import 'mapbox-gl-compare/dist/mapbox-gl-compare.css';
import React from 'react';

import {DisableClickableFeatures} from 'app/utils/mapUtils';

import cs from './styles.module.scss';

/**
 * Our own _syncCameras function, used only for our compare map. This replaces
 * mapbox-gl-compare's built-in sync (@mapbox/mapbox-gl-sync-move) for two
 * reasons:
 *
 *   1. It also mirrors PADDING. mapbox-gl-sync-move only mirrors center/zoom/
 *      /bearing/pitch. Without mirroring padding, the same geographic center renders
 *      at different screen positions on the two maps — a horizontal offset.
 *      Only the primary map (after-map) is ever driven by explicit fits
 *      (ie, ZoomCenterControl, grid zooming, zoomToProperty - see prop
 *      `isCameraLeader`. So the primary map is the only one that accumulates padding;
 *      mirroring it keeps the maps aligned.
 *
 *   2. It's bidirectional but animation-safe. The compare slider splits pointer
 *      events between the two maps (each map is interactive on its own side of
 *      the divider), so a user can pan/zoom either one and the other must
 *      follow. The off()/on() guard (identical to sync-move's) means the
 *      follower's mirrored `jumpTo` fires its `move` while listeners are
 *      detached, so it never feeds back to `jumpTo` (and thereby cancel) the
 *      leader's in-flight fitBounds animation. Mirroring everything — including
 *      padding — happens inside that guarded window, which is what makes it safe
 *      (doing it from a plain `move` listener does cancel the animation).
 *
 * Returns a teardown function that detaches the listeners.
 */
function _syncCameras(...maps: mapboxgl.Map[]): () => void {
  const copyCamera = (primary: mapboxgl.Map) => {
    const camera = {
      center: primary.getCenter(),
      zoom: primary.getZoom(),
      bearing: primary.getBearing(),
      pitch: primary.getPitch(),
      padding: primary.getPadding(),
    };
    maps.forEach((m) => {
      if (m !== primary) m.jumpTo(camera);
    });
  };

  const fns = maps.map((primary) => () => {
    off();
    copyCamera(primary);
    on();
  });

  const on = () => maps.forEach((m, i) => m.on('move', fns[i]));
  const off = () => maps.forEach((m, i) => m.off('move', fns[i]));

  // Always do an initial immediate alignment, in case a map was already moved
  // (e.g. a note-hash deep link fit the leader) before this sync was wired up.
  // The first map is theleader, so copy from it.
  on();
  copyCamera(maps[0]);

  return off;
}

export type CompareSliderSnapPosition = 'left' | 'right' | 'unset';

interface RenderMapArgs {
  className: string;
  onMapLoad: (map: mapboxgl.Map) => unknown;
}

interface Props {
  renderBeforeMap: (args: RenderMapArgs) => React.ReactNode;
  renderAfterMap: (args: RenderMapArgs) => React.ReactNode;

  /**
   * If true, moves the slider to only show the latest image. Used for when you
   * want to draw note geometry without the slider getting in the way.
   */
  moveSliderToOnlyShowLatestImage?: boolean;
  fullScreenOffset: number;
  compareSnapPosition: CompareSliderSnapPosition;
  setCompareSnapPosition: (position: CompareSliderSnapPosition) => void;
  controlPadding?: mapboxgl.PaddingOptions;
}

interface State {
  dragging: boolean;
}

export default class CompareMap extends React.Component<Props, State> {
  static defaultProps: Pick<Props, 'fullScreenOffset'> = {
    fullScreenOffset: 0,
  };

  state: State = {dragging: false};

  private beforeMap: mapboxgl.Map | null = null;
  private afterMap: mapboxgl.Map | null = null;

  private compareMap: MapboxCompare | null = null;
  private compareMapContainerRef = React.createRef<HTMLDivElement>();

  /** Tears down our custom camera sync (see maybeMakeCombinedMap). */
  private teardownSync: (() => void) | null = null;

  /**
   * Used to keep track of the position when we force the slider over with
   * moveSliderToOnlyShowLatestImage and fullScreenOffset props. If the user
   * doesn’t move the slider at all then we’ll reset to this position when
   * moveSliderToOnlyShowLatestImage is turned off.
   */
  private savedSliderPosition: number | null = null;

  private maybeMakeCombinedMap() {
    const {beforeMap, afterMap} = this;
    const {moveSliderToOnlyShowLatestImage} = this.props;

    if (beforeMap && afterMap && !this.compareMap && this.compareMapContainerRef.current) {
      this.compareMap = new MapboxCompare(beforeMap, afterMap, this.compareMapContainerRef.current);
      this.compareMap.on('slideend', this.onSlideEnd);

      // Replace mapbox-gl-compare's camera sync with our own (see _syncCameras).
      // Detach the lib's sync via its stored teardown handle, then stub it so the
      // lib's own remove() won't double-clear. `_clearSync` is a private field on
      // the pinned, unmaintained 0.4.1 dep; guarded in case internals change.
      const compareMapInternals = this.compareMap as unknown as {_clearSync?: () => void};
      if (typeof compareMapInternals._clearSync === 'function') {
        compareMapInternals._clearSync();
        compareMapInternals._clearSync = () => {};
      }
      this.teardownSync = _syncCameras(afterMap, beforeMap);

      if (moveSliderToOnlyShowLatestImage) {
        this.compareMap.setSlider(this.getFullScreenLatestPosition());
      }

      beforeMap.resize();
      afterMap.resize();
    }
  }

  componentDidUpdate(oldProps: Props) {
    if (this.compareMap) {
      if (!oldProps.moveSliderToOnlyShowLatestImage && this.props.moveSliderToOnlyShowLatestImage) {
        // This is the case where we need to “full screen the latest,” meaning
        // move the slider all the way to the left so that the user can draw
        // something (which doesn’t work well across compare mode’s divider).
        this.savedSliderPosition = this.compareMap.currentPosition;
        this.compareMap.setSlider(this.getFullScreenLatestPosition());
      } else if (
        oldProps.moveSliderToOnlyShowLatestImage &&
        !this.props.moveSliderToOnlyShowLatestImage
      ) {
        // Here we’re no longer full screening the latest scene, so if the user
        // didn’t move the slider at all, reset to where it was before we moved
        // it.
        if (this.savedSliderPosition !== null) {
          this.compareMap.setSlider(this.savedSliderPosition);
          this.savedSliderPosition = null;
        }
      }

      // If an adjustment comes in when `fullScreenLatest` is already
      // `true`, set the slider position. This allows us to push the compare
      // slider all the way to the left from switching directly from the
      // TabbedSidebar to the AnalyzePolygonTool.
      if (
        this.props.moveSliderToOnlyShowLatestImage &&
        oldProps.fullScreenOffset !== this.props.fullScreenOffset
      ) {
        this.compareMap.setSlider(this.getFullScreenLatestPosition());
      }

      if (
        oldProps.compareSnapPosition !== this.props.compareSnapPosition &&
        this.props.compareSnapPosition !== 'unset'
      ) {
        this.snapSlider(this.props.compareSnapPosition);
      }

      // If padding ever changes, make sure that the slider is still visible.
      if (!isEqual(oldProps.controlPadding, this.props.controlPadding)) {
        this.ensureSliderVisible();
      }
    }
  }

  componentWillUnmount() {
    this.compareMap?.off('slideend', this.onSlideEnd);
    this.teardownSync?.();
  }

  onPointerDown = (ev: React.PointerEvent<HTMLElement>) => {
    if (
      ev.target instanceof HTMLElement &&
      ev.target.classList.contains('compare-swiper-vertical')
    ) {
      this.props.setCompareSnapPosition('unset');
      this.setState({dragging: true});
    }
  };

  onSlideEnd = () => {
    this.setState({dragging: false});

    // We reset the saved slider position so that if you drag the slider in“full
    // screen latest” mode we leave it where you put it once that mode ends.
    this.savedSliderPosition = null;

    // This fixes if you drag the slider under the sidebar or something.
    this.ensureSliderVisible();
  };

  private getFullScreenLatestPosition() {
    return (this.props.controlPadding?.left ?? 0) + this.props.fullScreenOffset;
  }

  private snapSlider(position: 'right' | 'left') {
    const {controlPadding} = this.props;
    const {compareMap, compareMapContainerRef} = this;

    if (
      controlPadding?.right === undefined ||
      controlPadding?.left === undefined ||
      controlPadding?.right === null ||
      controlPadding?.left === null ||
      !compareMap ||
      !compareMapContainerRef.current
    ) {
      return;
    }

    const offset =
      position === 'right'
        ? compareMapContainerRef.current.clientWidth - controlPadding.right
        : controlPadding.left;
    compareMap.setSlider(offset);
  }

  private ensureSliderVisible() {
    const {controlPadding} = this.props;
    const {compareMap, compareMapContainerRef} = this;

    if (
      controlPadding?.left === undefined ||
      controlPadding?.right === undefined ||
      controlPadding?.right === null ||
      controlPadding?.left === null ||
      !compareMap ||
      !compareMapContainerRef.current
    ) {
      return;
    }

    if (compareMap.currentPosition < controlPadding.left) {
      compareMap.setSlider(controlPadding.left);
    } else if (
      compareMap.currentPosition >
      compareMapContainerRef.current.clientWidth - controlPadding.right
    ) {
      compareMap.setSlider(compareMapContainerRef.current.clientWidth - controlPadding.right);
    }
  }

  render() {
    const {renderBeforeMap, renderAfterMap} = this.props;
    const {dragging} = this.state;

    return (
      <div
        className={cs.compareMapContainer}
        ref={this.compareMapContainerRef}
        onPointerDown={this.onPointerDown}
      >
        {renderBeforeMap({
          className: cs.compareMap,
          onMapLoad: (beforeMap) => {
            this.beforeMap = beforeMap;
            this.maybeMakeCombinedMap();
          },
        })}

        {renderAfterMap({
          className: cs.compareMap,
          onMapLoad: (afterMap) => {
            this.afterMap = afterMap;
            this.maybeMakeCombinedMap();
          },
        })}

        {dragging && <DisableClickableFeatures />}
      </div>
    );
  }
}
